/**
 * The lookups the targeting editor searches against: the network's keyword and
 * category profiles, ad unit matching labels, the location tree, site groups,
 * segments and the semantic article index. Sites, ad units, publishers and device properties come from
 * their own services.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { getContext } from '@/services/core/context'
import type { ListPage } from '@/services/core/paging'
import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import type {
  MunicipalityRef,
  NamedLocationRef,
  ObjectRef,
  SemanticSentence,
  Targeting,
} from '@/services/targeting/targeting'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

async function get(path: string, params: Record<string, string> = {}): Promise<unknown> {
  const query = new URLSearchParams({ context: getContext(), ...params })
  const res = await apiFetch(`${API_BASE}/${path}?${query}`, { headers: JSON_HEADERS })
  if (!res.ok) throw new Error(`Failed to fetch ${path} (${res.status})`)
  return res.json()
}

// ─── Keyword and category profiles ──────────────────────────────────────────

interface SearchPage<T> {
  results: T[]
  totalHits: number
}

async function searchPage<T>(
  path: string,
  map: (row: Record<string, unknown>) => T,
  params: Record<string, string>
): Promise<SearchPage<T>> {
  const data = (await get(path, params)) as Record<string, unknown>
  const rows = (data.searchResults as Record<string, unknown>[] | undefined) ?? []
  return { results: rows.map(map), totalHits: (data.totalHits as number) ?? rows.length }
}

/** A keyword the network has seen, with how often it has been seen. */
export interface KeywordSuggestion {
  keyword: string
  frequency?: number
}

export const KeywordsApi = {
  /** Without a search term the most frequent keywords come back first. */
  search: (q: string, pageSize = 25) =>
    searchPage<KeywordSuggestion>(
      'keywords',
      (r) => ({ keyword: (r.keyword as string) ?? '', frequency: r.frequency as number }),
      {
        q,
        pageSize: String(pageSize),
        ...(q ? {} : { orderBy: 'FREQUENCY', orderByDirection: 'DESCENDING' }),
      }
    ),
}

/** A category the network has seen, with how often it has been seen. */
export interface CategorySuggestion {
  category: string
  categoryType?: string
  frequency?: number
}

export const CategoriesApi = {
  search: (q: string, pageSize = 25) =>
    searchPage<CategorySuggestion>(
      'categories',
      (r) => ({
        category: (r.category as string) ?? '',
        categoryType: r.categoryType as string,
        frequency: r.frequency as number,
      }),
      { q, pageSize: String(pageSize) }
    ),
}

// ─── Ad unit matching labels ────────────────────────────────────────────────

/**
 * The matching labels ad units carry, from the search index — there is no
 * matching-label endpoint of its own, so they are searched as objects of type
 * `MatchingLabel` the way the ad unit editor's label picker does.
 */
export const MatchingLabelsApi = {
  search: async (q = '', pageSize = 50): Promise<string[]> => {
    const term = q.trim()
    const { results } = await searchPage<string>('search', (r) => (r.name as string) ?? '', {
      types: 'MatchingLabel',
      objectState: 'ACTIVE',
      pageSize: String(pageSize),
      // A one or two letter term matches far more wildcarded than exact.
      q: term.length > 0 && term.length < 3 && !term.endsWith('*') ? `${term}*` : term,
      ...(term ? {} : { orderBy: 'NAME' }),
    })
    // The index can return the same label once per ad unit carrying it.
    return [...new Set(results.filter(Boolean))]
  },
}

// ─── Locations ──────────────────────────────────────────────────────────────

export interface LocationSearchParams {
  q?: string
  /** Location type to restrict to, e.g. `CITY` or `CONTINENT`. */
  types?: string
  /** Only locations inside this country (ISO code). */
  countryId?: string
  /** Only the children of this location — how the tree is walked. */
  parent?: string
  page?: number
  pageSize?: number
}

/** How many matches a search found of one location type. */
export interface LocationTypeCount {
  type: string
  count: number
}

export interface LocationSearchPage extends SearchPage<NamedLocationRef> {
  /**
   * The breakdown of the matches by location type, straight from the search
   * index's own `types` aggregation — countries, regions, cities and postcodes
   * counted separately so a wide search can be narrowed to one kind.
   */
  typeCounts: LocationTypeCount[]
}

const mapLocation = (r: Record<string, unknown>): NamedLocationRef => ({
  id: r.id as number,
  name: r.name as string,
  type: r.type as string,
  context: r.context as string,
  active: r.active as boolean,
  ipReach: r.ipReach as string,
})

export const LocationsApi = {
  search: async ({
    q = '',
    types,
    countryId,
    parent,
    page = 1,
    pageSize = 25,
  }: LocationSearchParams): Promise<LocationSearchPage> => {
    const data = (await get('location', {
      q,
      pageSize: String(pageSize),
      // The location endpoint pages from zero, unlike the object lists.
      page: String(Math.max(0, page - 1)),
      ...(types ? { types } : {}),
      ...(countryId ? { countryId } : {}),
      ...(parent ? { parent } : {}),
      // An unordered query returns the tree in its own order, which is what
      // walking continents down to postcodes wants.
      ...(q ? {} : { blankOrderBy: 'true' }),
    })) as Record<string, unknown>
    const rows = (data.searchResults as Record<string, unknown>[] | undefined) ?? []
    // Only a query carries aggregations — a `parent` walk is served from the
    // location store, which counts children but does not bucket them.
    const buckets = ((data.aggregations as Record<string, { buckets?: Record<string, unknown> }>)
      ?.types?.buckets ?? {}) as Record<string, { filter?: string; count?: number }>
    return {
      results: rows.map(mapLocation),
      totalHits: (data.totalHits as number) ?? rows.length,
      typeCounts: Object.entries(buckets)
        .map(([filter, bucket]) => ({
          type: bucket.filter ?? filter,
          count: bucket.count ?? 0,
        }))
        .filter((entry) => entry.count > 0)
        .sort((a, b) => b.count - a.count),
    }
  },

  /**
   * The locations directly inside one location — a continent's countries, a
   * country's regions, and so on down to postcodes. This is the tree walk, so
   * it takes whole pages: the browser column shows the lot rather than a top
   * few, the way the old UI's 200-a-page tree did.
   */
  children: (parent: number, pageSize = 200) =>
    LocationsApi.search({ parent: String(parent), pageSize }),

  /** The continents — the roots the location tree is walked from. */
  roots: (pageSize = 200) => LocationsApi.search({ types: 'CONTINENT', pageSize }),

  /** The municipalities (Swedish kommuner, Norwegian kommuner…) of a country. */
  municipalities: async (country: string): Promise<MunicipalityRef[]> => {
    const data = await get('location/municipalities', { country })
    return toArray<Record<string, unknown>>(data)
      .map((r) => ({ id: r.id as number, name: r.name as string }))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  },
}

// ─── Site groups and segments ───────────────────────────────────────────────

async function objectList(path: string, params: Record<string, string> = {}): Promise<ObjectRef[]> {
  const data = await get(path, { pageSize: '100', page: '1', orderBy: 'name', ...params })
  return toArray<Record<string, unknown>>(data).map((r) => ({
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: r.description as string | undefined,
  }))
}

export const SiteGroupsApi = {
  getAll: (search?: string) =>
    objectList('sitegroups', search ? { filterBy: 'name;description', filterByLike: search } : {}),
}

/** The kinds of audience a segment can describe — `UserSegment.Type` in core. */
export const SEGMENT_TYPES = ['GEO', 'INTERESTS', 'BEHAVIOUR', 'DEMOGRAPHIC', 'INTENT'] as const
export type SegmentType = (typeof SEGMENT_TYPES)[number]

/** A segment, with the data source it was uploaded from. */
export interface SegmentRef extends ObjectRef {
  /** `CXENSE`, `ADOBE`, `FIRST_PARTY`… — absent for a segment made by hand. */
  dataSource?: string
  type?: SegmentType
  /** The publishers offering the segment, on a marketplace. */
  publishers?: ObjectRef[]
  /** What a premium segment costs on top of the line item's price. */
  cpm?: CurrencyAmount
  objectState?: string
}

function mapSegment(r: Record<string, unknown>): SegmentRef {
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: r.description as string | undefined,
    dataSource: r.dataSource as string | undefined,
    type: r.type as SegmentType | undefined,
    publishers: ((r.publishers as Record<string, unknown>[] | undefined) ?? []).map((p) => ({
      id: (p.id as string) ?? '',
      name: p.name as string | undefined,
    })),
    cpm: r.cpm as CurrencyAmount | undefined,
    objectState: r.objectState as string | undefined,
  }
}

export interface SegmentSearchParams {
  q?: string
  type?: SegmentType
  /** Only the segments this publisher offers. */
  publisherId?: string
  /**
   * Only the segments of the publishers this targeting reaches, as its
   * publisher, site, ad unit and site country targets narrow them
   * (`SegmentsResource.withTargeting`). The server then ignores `publisherId`.
   */
  targeting?: Targeting
  page?: number
  pageSize?: number
}

export const SegmentsApi = {
  /**
   * A segment uploaded from a data source is created from its external id
   * alone (`SegmentsResource` calls `getOrCreate(externalId)`) and carries no
   * name, so the list can be a column of ids — which are then the only thing
   * there is to search on. `externalId` is therefore one of the searched
   * columns, and the data source comes back too, so a nameless row still says
   * where it came from, the way the old UI's segment table does.
   */
  search: async ({
    q = '',
    type,
    publisherId,
    targeting,
    page = 1,
    pageSize = 50,
  }: SegmentSearchParams): Promise<ListPage<SegmentRef>> => {
    const params = {
      pageSize: String(pageSize),
      page: String(page),
      orderBy: 'name',
      ...(q ? { filterBy: 'name;description;externalId', filterByLike: q } : {}),
      ...(type ? { type } : {}),
      ...(publisherId ? { publisherIds: publisherId } : {}),
    }
    let data: { totalCount?: number }
    if (targeting) {
      const query = new URLSearchParams({
        context: getContext(),
        format: 'json',
        concise: 'true',
        ...params,
      })
      // Semantic targets say nothing about publishers, and are left out the
      // way the old UI leaves them out.
      const body: Targeting = { ...targeting }
      delete body.semanticTargets
      const res = await apiFetch(`${API_BASE}/segments/withtargeting?${query}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(`Failed to search segments (${res.status})`)
      data = await res.json()
    } else {
      data = (await get('segments', params)) as { totalCount?: number }
    }
    const results = toArray<Record<string, unknown>>(data).map(mapSegment)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /**
   * The named segments behind a list of ids. A network can hold far more
   * segments than one page of {@link getAll}, so the segments a line item
   * targets are asked for by id rather than looked for in that page — the way
   * the old UI hydrates its targeting when the segment tab opens.
   */
  getByIds: async (ids: string[]): Promise<SegmentRef[]> => {
    if (ids.length === 0) return []
    const data = await get('segments', { id: ids.join(';'), pageSize: String(ids.length) })
    return toArray<Record<string, unknown>>(data).map(mapSegment)
  },
}

// ─── Pre-defined key-values ─────────────────────────────────────────────────

/** A key-value pair the network has uploaded as reference data. */
export interface KeyValueSuggestion {
  key: string
  values: string[]
}

export interface KeyValueSearchPage extends SearchPage<KeyValueSuggestion> {
  /** Zero-based, as the API takes it. */
  page: number
  pageSize: number
}

export const KeyValuesApi = {
  /** Reference-data key-values, searched free-text and paged like the API pages them. */
  search: async (q: string, page = 0, pageSize = 25): Promise<KeyValueSearchPage> => {
    const found = await searchPage<KeyValueSuggestion>(
      'keyvalues',
      (r) => ({ key: (r.key as string) ?? '', values: (r.values as string[]) ?? [] }),
      { q, page: String(page), pageSize: String(pageSize) }
    )
    return { ...found, page, pageSize }
  },
}

// ─── Keyword article preview ────────────────────────────────────────────────

/** An article a keyword target matches, with the keywords it matched on. */
export interface KeywordArticle extends SemanticArticle {
  /** The article's own keywords, minus the ones it matched on. */
  keywords?: string[]
  /** The targeted keywords this article carries. */
  matched?: string[]
}

export interface KeywordArticlePage {
  results: KeywordArticle[]
  totalCount: number
}

export const KeywordArticlesApi = {
  /**
   * The articles the network has seen that a keyword target matches, narrowed
   * to the sites the rest of the targeting reaches.
   *
   * The endpoint takes exactly one keyword target, so the caller passes the
   * targeting with the one being edited in place of whatever it holds.
   */
  search: async (
    targeting: Targeting,
    keywords: string[],
    page = 1,
    pageSize = 25
  ): Promise<KeywordArticlePage> => {
    const query = new URLSearchParams({
      context: getContext(),
      page: String(page),
      pageSize: String(pageSize),
    })
    const body: Targeting = {
      ...targeting,
      keywordTargets: [{ ...(targeting.keywordTargets?.[0] ?? {}), keywords }],
    }
    const res = await apiFetch(`${API_BASE}/targetingquery/keywordprofiles2?${query}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`Failed to search articles (${res.status})`)
    const data = (await res.json()) as KeywordArticlePage
    return { results: data.results ?? [], totalCount: data.totalCount ?? 0 }
  },
}

// ─── Semantic targeting ─────────────────────────────────────────────────────

/** An article the semantic index matched against a sentence. */
export interface SemanticArticle {
  /** Host and path, without the scheme. */
  url: string
  title?: string
  description?: string
  image?: string
  publishedTime?: string
  /** How close the article's meaning is to the sentence. */
  score?: number
  /** At or below 0 reads as negative, above 1 as positive. */
  sentimentScore?: number
  availableImpressionsLastDay?: number
}

export interface SemanticArticleSearch {
  sentence: string
  score: number
  positiveSentimentOnly?: boolean
  /** Language codes to restrict the articles to; empty searches them all. */
  languages?: string[]
  /** Also count every match, not only the page returned — a slower query. */
  totalHits?: boolean
  pageSize?: number
}

export interface SemanticArticlePage {
  results: SemanticArticle[]
  /** Every matching page, when `totalHits` was asked for and there were more than one page of. */
  totalCount: number
  totalAvailableImpressionsLastDay?: number
}

export const SemanticApi = {
  /**
   * Encodes a sentence and stores it, returning the id a semantic target must
   * carry. The same text always comes back with the same id.
   */
  embed: async (sentence: string): Promise<SemanticSentence> => {
    const data = (await get('targetingquery/embedding', { sentence })) as SemanticSentence
    return { id: data.id, sentence: data.sentence }
  },

  /**
   * The articles, busiest first, whose meaning matches a sentence closely
   * enough. The targeting narrows the search to the sites it reaches; its own
   * semantic targets are left out, or the search would only find what they
   * already match.
   */
  searchArticles: async (
    targeting: Targeting,
    search: SemanticArticleSearch
  ): Promise<SemanticArticlePage> => {
    const query = new URLSearchParams({
      context: getContext(),
      sentence: search.sentence,
      thresholdScore: String(search.score),
      positiveSentimentOnly: String(!!search.positiveSentimentOnly),
      languages: (search.languages ?? []).join(';'),
      totalHits: String(!!search.totalHits),
      page: '1',
      pageSize: String(search.pageSize ?? 100),
      concise: 'true',
    })
    const body: Targeting = { ...targeting }
    delete body.semanticTargets
    const res = await apiFetch(`${API_BASE}/targetingquery/articles?${query}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`Failed to search articles (${res.status})`)
    const data = (await res.json()) as SemanticArticlePage
    return {
      results: data.results ?? [],
      totalCount: data.totalCount ?? 0,
      totalAvailableImpressionsLastDay: data.totalAvailableImpressionsLastDay,
    }
  },
}
